import React, { Fragment } from 'react';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Label } from 'recharts';
import { FigureCard } from './FigureCard';
const data = [{
  t: 60,
  eta: 10.5
}, {
  t: 70,
  eta: 13.1
}, {
  t: 80,
  eta: 15.6
}, {
  t: 90,
  eta: 17.9
}, {
  t: 100,
  eta: 20.1
}];
const increments = [{
  range: '60→70°C',
  delta: '+2.6 pp'
}, {
  range: '70→80°C',
  delta: '+2.5 pp'
}, {
  range: '80→90°C',
  delta: '+2.3 pp'
}, {
  range: '90→100°C',
  delta: '+2.2 pp'
}];
const SquareDot = (props: any) => {
  const {
    cx,
    cy
  } = props;
  return <rect x={cx - 5} y={cy - 5} width={10} height={10} fill="#1A4A68" stroke="#fff" strokeWidth={1.5} data-id="element-91" />;
};
export function Figure2() {
  return <FigureCard number={2} title="Carnot Efficiency as a Function of Heat Source Temperature" subtitle="Calculated using η_Carnot = 1 − T_cold/T_hot (K). T_cold = 298.15 K throughout." caption="The Carnot curve is concave, reflecting diminishing returns: each additional 10 °C of T_hot yields slightly less efficiency gain. Reference dashed lines show real-world geothermal plant efficiencies for context." data-id="element-92">
      <div className="relative" data-id="element-93">
        <div className="h-[460px] w-full" data-id="element-94">
          <ResponsiveContainer data-id="element-95">
            <ComposedChart data={data} margin={{
            top: 30,
            right: 200,
            left: 30,
            bottom: 50
          }} data-id="element-96">
              <defs data-id="element-97">
                <linearGradient id="f2Fill" x1="0" y1="0" x2="0" y2="1" data-id="element-98">
                  <stop offset="0%" stopColor="#1A4A68" stopOpacity={0.3} data-id="element-99" />
                  <stop offset="100%" stopColor="#1A4A68" stopOpacity={0.04} data-id="element-100" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E5E0D6" strokeDasharray="2 4" vertical={false} data-id="element-101" />
              <XAxis dataKey="t" type="number" domain={[55, 105]} ticks={[60, 70, 80, 90, 100]} axisLine={{
              stroke: '#1B1B1B'
            }} tick={{
              fontSize: 12,
              fill: '#374151'
            }} data-id="element-102">
                <Label value="Heat Source Temperature, T_hot (°C)" position="bottom" offset={20} style={{
                fill: '#1B1B1B',
                fontSize: 13,
                fontFamily: 'Inter',
                fontWeight: 500
              }} data-id="element-103" />
              </XAxis>
              <YAxis domain={[0, 25]} ticks={[0, 5, 10, 15, 20, 25]} axisLine={{
              stroke: '#1B1B1B'
            }} tick={{
              fontSize: 12,
              fill: '#374151'
            }} tickFormatter={v => `${v}%`} data-id="element-104">
                <Label value="Carnot Efficiency, η_Carnot (%)" angle={-90} position="left" offset={10} style={{
                fill: '#1B1B1B',
                fontSize: 13,
                fontFamily: 'Inter',
                fontWeight: 500,
                textAnchor: 'middle'
              }} data-id="element-105" />
              </YAxis>
              <Tooltip contentStyle={{
              background: '#fff',
              border: '1px solid #1A4A68',
              fontFamily: 'Inter',
              fontSize: 12
            }} formatter={(v: any) => [`${v}%`, 'η_Carnot']} labelFormatter={l => `T_hot = ${l} °C`} data-id="element-106" />

              {/* Reference lines for real plants */}
              <ReferenceLine y={8} stroke="#2E5E28" strokeDasharray="6 4" strokeWidth={1.5} data-id="element-107">
                <Label value="Iceland binary cycle (8%)" position="right" offset={8} style={{
                fill: '#2E5E28',
                fontSize: 11,
                fontFamily: 'Inter',
                fontWeight: 500
              }} data-id="element-108" />
              </ReferenceLine>
              <ReferenceLine y={15} stroke="#EF9F27" strokeDasharray="6 4" strokeWidth={1.5} data-id="element-109">
                <Label value="New Zealand flash steam (15%)" position="right" offset={8} style={{
                fill: '#B97400',
                fontSize: 11,
                fontFamily: 'Inter',
                fontWeight: 500
              }} data-id="element-110" />
              </ReferenceLine>
              <ReferenceLine y={21} stroke="#8B2500" strokeDasharray="6 4" strokeWidth={1.5} data-id="element-111">
                <Label value="The Geysers, CA (21%)" position="right" offset={8} style={{
                fill: '#8B2500',
                fontSize: 11,
                fontFamily: 'Inter',
                fontWeight: 500
              }} data-id="element-112" />
              </ReferenceLine>

              <Area type="monotone" dataKey="eta" stroke="none" fill="url(#f2Fill)" isAnimationActive={false} data-id="element-113" />
              <Line type="monotone" dataKey="eta" stroke="#1A4A68" strokeWidth={2.5} dot={<SquareDot data-id="element-115" />} isAnimationActive={false} data-id="element-114" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Theoretical max note */}
        <div className="absolute top-[8px] left-[50%] -translate-x-1/2 pointer-events-none" data-id="element-116">
          <div className="font-sans text-[10px] text-[#5B5448] italic" data-id="element-117">
            Theoretical max (100%) — unattainable, far above this scale ↑
          </div>
        </div>

        {/* Formula box */}
        <div className="absolute top-[60px] left-[80px] bg-white border-2 border-[#1A4A68] rounded-sm px-4 py-3 shadow-sm" data-id="element-118">
          <div className="font-sans text-[9px] uppercase tracking-wider text-[#1A4A68] font-semibold mb-1" data-id="element-119">
            Carnot's Theorem
          </div>
          <div className="font-mono text-[13px] text-[#1B1B1B]" data-id="element-120">
            η<sub data-id="element-121">Carnot</sub> = 1 − T<sub data-id="element-122">cold</sub>/T<sub data-id="element-123">hot</sub>
          </div>
          <div className="font-mono text-[11px] text-[#5B5448] mt-1" data-id="element-124">
            T<sub data-id="element-125">cold</sub> = 298.15 K
          </div>
        </div>

        {/* Diminishing returns table */}
        <div className="grid grid-cols-2 gap-3 mt-6 mx-2" data-id="element-126">
          <div className="bg-[#F5F8FB] border border-[#1A4A68]/30 rounded-sm p-4" data-id="element-127">
            <div className="font-sans text-[10px] uppercase tracking-wider text-[#1A4A68] font-semibold mb-2" data-id="element-128">
              Diminishing returns — Δη per 10 °C
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-[12px]" data-id="element-129">
              {increments.map(it => <Fragment key={it.range} data-id="element-130">
                  <span className="text-[#1B1B1B]" data-id="element-131">{it.range}</span>
                  <span className="text-[#8B2500] font-semibold" data-id="element-132">
                    {it.delta}
                  </span>
                </Fragment>)}
            </div>
          </div>
          <div className="bg-[#FEF3C7]/40 border border-[#EF9F27]/40 rounded-sm p-4 font-serif text-[12px] text-[#1B1B1B] leading-relaxed" data-id="element-133">
            <span className="font-semibold" data-id="element-134">Interpretation: </span>
            Although η<sub data-id="element-135">Carnot</sub> rises monotonically with T<sub data-id="element-136">hot</sub>
            , the marginal gain per 10 °C falls from{' '}
            <span className="font-mono text-[#1A4A68]" data-id="element-137">+2.6 pp</span> to{' '}
            <span className="font-mono text-[#1A4A68]" data-id="element-138">+2.2 pp</span>. This is
            the geometry of the Second Law: η → 1 only asymptotically.
          </div>
        </div>
      </div>
    </FigureCard>;
}